import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardBody, Row, Col, Button, Table, Modal, ModalBody } from "reactstrap";
import { ClipLoader } from "react-spinners";
import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { formatDateLabel } from "../../../utils/dateOnly";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import PageHero from "../../../components/PageHero";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotInsetCardStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";

// Canais de recepção (Sustentação). Veio da Auditoria de CT-e, onde estava
// como card "Pendente de integração" e confundia: o número era a quantidade de
// canais SFTP configurados (TransportadoraLocalArquivo tipo 7 ativo), não de
// arquivos. Aqui a tela fala o que é: canal por transportadora, atividade
// recente e, por canal, os arquivos ainda não integrados (listagem ao vivo no
// SFTP) e o histórico de recepção.

const STATUS_CANAL = {
  ativo: { label: "Ativo", short: "Recebeu arquivo nas últimas 24h", color: "var(--everlog-status-success)" },
  recente: { label: "Recente", short: "Recebeu arquivo nos últimos 7 dias", color: "var(--everlog-status-warning)" },
  inativo: { label: "Inativo", short: "Entre 8 e 30 dias sem arquivo", color: "var(--everlog-status-info)" },
  silencioso: { label: "Silencioso", short: "Mais de 30 dias sem arquivo, ou nunca recebeu", color: AUTOPILOT_COLORS.muted },
};

const STATUS_ORDER = ["ativo", "recente", "inativo", "silencioso"];

const SITUACAO_RECEPCAO = {
  1: { label: "Importado", color: "var(--everlog-status-success)" },
  2: { label: "Tentativa", color: "var(--everlog-status-warning)" },
  4: { label: "Rejeitado", color: "var(--everlog-status-danger)" },
  6: { label: "Cancelado", color: "var(--everlog-status-neutral)" },
  7: { label: "Cancel. falhou", color: "var(--everlog-status-danger)" },
};

const metricCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.85rem 0.95rem",
  background: "var(--everlog-card-surface-field)",
  height: "100%",
};

const metricLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.74rem",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "0.3rem",
};

const metricValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.08rem",
  fontWeight: 700,
  lineHeight: 1.2,
};

const tableHeadCellStyle = {
  color: AUTOPILOT_COLORS.muted,
  borderColor: AUTOPILOT_COLORS.border,
  fontWeight: 600,
  background: "var(--everlog-cte-table-head-bg)",
  position: "sticky",
  top: 0,
  zIndex: 1,
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  color: AUTOPILOT_COLORS.text,
  borderColor: AUTOPILOT_COLORS.border,
  verticalAlign: "middle",
  background: "var(--everlog-card-surface-field)",
};

const pillStyle = (active, color) => ({
  borderRadius: 999,
  border: `1px solid ${active ? color : AUTOPILOT_COLORS.border}`,
  background: active
    ? `color-mix(in srgb, ${color} 20%, var(--everlog-card-surface-field))`
    : "var(--everlog-card-surface-field)",
  color: active ? AUTOPILOT_COLORS.title : AUTOPILOT_COLORS.text,
  fontSize: "0.78rem",
  fontWeight: 600,
  padding: "0.38rem 0.75rem",
  cursor: "pointer",
  boxShadow: active ? "var(--everlog-card-interactive-shadow)" : "none",
});

const emptyModalState = {
  open: false,
  canal: null,
  tab: "pendentes",
  pendentes: [],
  loadingPendentes: false,
  errorPendentes: null,
  recepcoes: [],
  loadingRecepcoes: false,
  errorRecepcoes: null,
};

function StatusBadge({ status }) {
  const meta = STATUS_CANAL[status] || STATUS_CANAL.silencioso;
  return (
    <span
      title={meta.short}
      style={{
        display: "inline-block",
        borderRadius: 999,
        padding: "0.18rem 0.55rem",
        fontSize: "0.71rem",
        fontWeight: 600,
        color: meta.color,
        border: `1px solid ${meta.color}`,
        background: `color-mix(in srgb, ${meta.color} 13%, transparent)`,
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("pt-BR");
}

export default function CanaisRecepcao() {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const empresaId = empresa && empresa.value;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [modal, setModal] = useState(emptyModalState);

  const load = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/sustentacao/canais-recepcao", {
        params: { embarcadorId: empresaId },
      });
      setData(response.data || null);
    } catch (err) {
      setData(null);
      setError(err.response?.data?.error || err.message || "Erro ao carregar canais de recepção.");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    if (!empresaId) {
      setData(null);
      return;
    }
    load();
  }, [empresaId, load]);

  const canais = useMemo(() => {
    const lista = data?.canais || [];
    const filtrada = statusFilter ? lista.filter((c) => c.status === statusFilter) : lista;
    // Silenciosos primeiro: é o que a sustentação precisa olhar.
    return [...filtrada].sort((a, b) => {
      const sa = STATUS_ORDER.indexOf(a.status);
      const sb = STATUS_ORDER.indexOf(b.status);
      if (sa !== sb) return sb - sa;
      return String(a.transportadora || "").localeCompare(String(b.transportadora || ""), "pt-BR");
    });
  }, [data, statusFilter]);

  const contagemPorStatus = useMemo(() => {
    const out = { ativo: 0, recente: 0, inativo: 0, silencioso: 0 };
    (data?.canais || []).forEach((c) => {
      out[c.status] = (out[c.status] || 0) + 1;
    });
    return out;
  }, [data]);

  const abrirCanal = async (canal) => {
    setModal({ ...emptyModalState, open: true, canal, loadingPendentes: true });
    try {
      const response = await api.get(`/sustentacao/canais-recepcao/${canal.canalId}/pendentes`, {
        params: { embarcadorId: empresaId },
      });
      const payload = response.data || {};
      setModal((prev) => ({
        ...prev,
        pendentes: payload.pendentes || [],
        loadingPendentes: false,
        errorPendentes: payload.error || null,
      }));
    } catch (err) {
      setModal((prev) => ({
        ...prev,
        loadingPendentes: false,
        errorPendentes: err.response?.data?.error || err.message || "Erro ao listar arquivos pendentes.",
      }));
    }
  };

  const trocarAbaModal = async (tab) => {
    if (tab === "historico" && modal.recepcoes.length === 0 && !modal.loadingRecepcoes) {
      setModal((prev) => ({ ...prev, tab, loadingRecepcoes: true, errorRecepcoes: null }));
      try {
        const response = await api.get(`/sustentacao/canais-recepcao/${modal.canal.canalId}/recepcoes`, {
          params: { embarcadorId: empresaId },
        });
        setModal((prev) => ({ ...prev, recepcoes: response.data || [], loadingRecepcoes: false }));
      } catch (err) {
        setModal((prev) => ({
          ...prev,
          loadingRecepcoes: false,
          errorRecepcoes: err.response?.data?.error || err.message || "Erro ao carregar histórico.",
        }));
      }
      return;
    }
    setModal((prev) => ({ ...prev, tab }));
  };

  const fecharModal = () => setModal(emptyModalState);

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  return (
    <div className="content">
      <PageHero
        eyebrow="Sustentação"
        title="Canais de recepção"
        description="Canais SFTP configurados por transportadora, atividade recente e arquivos ainda não integrados."
        actions={
          <Button className="btn-sm" style={autopilotSecondaryButtonStyle} onClick={load} disabled={loading}>
            {loading ? <ClipLoader size={12} color="var(--everlog-on-accent-text)" /> : "↺ Atualizar"}
          </Button>
        }
      />

      <Card style={autopilotCardStyle}>
        <CardBody style={autopilotBodyStyle}>
          {error ? (
            <div style={{ color: "var(--everlog-status-danger)", fontSize: "0.85rem", marginBottom: "0.75rem" }}>{error}</div>
          ) : null}

          <Row style={{ marginBottom: "0.75rem" }}>
            <Col md={4} style={{ marginBottom: 12 }}>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>Canais configurados</div>
                <div style={{ ...metricValueStyle, color: "var(--everlog-status-info)" }}>
                  {data ? data.totalCanais : "-"}
                </div>
              </div>
            </Col>
            <Col md={4} style={{ marginBottom: 12 }}>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>Ativos (últimos 7 dias)</div>
                <div style={{ ...metricValueStyle, color: "var(--everlog-status-success)" }}>
                  {data ? data.canaisAtivos : "-"}
                </div>
              </div>
            </Col>
            <Col md={4} style={{ marginBottom: 12 }}>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>Silenciosos (30 dias ou mais)</div>
                <div style={{ ...metricValueStyle, color: "var(--everlog-status-danger)" }}>
                  {data ? data.canaisSilenciosos : "-"}
                </div>
              </div>
            </Col>
          </Row>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.85rem" }}>
            <button type="button" style={pillStyle(!statusFilter, AUTOPILOT_COLORS.accent)} onClick={() => setStatusFilter(null)}>
              Todos{data ? ` (${data.totalCanais})` : ""}
            </button>
            {STATUS_ORDER.map((key) => (
              <button
                key={key}
                type="button"
                title={STATUS_CANAL[key].short}
                style={pillStyle(statusFilter === key, STATUS_CANAL[key].color)}
                onClick={() => setStatusFilter(statusFilter === key ? null : key)}
              >
                {STATUS_CANAL[key].label} ({contagemPorStatus[key] || 0})
              </button>
            ))}
          </div>

          {loading && !data ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
              <ClipLoader size={28} color={AUTOPILOT_COLORS.accent} />
            </div>
          ) : canais.length === 0 ? (
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", padding: "0.5rem 0" }}>
              {data && data.totalCanais > 0
                ? "Nenhum canal nesse status."
                : "Nenhum canal SFTP configurado para esta empresa."}
            </div>
          ) : (
            <div style={{ maxHeight: "62vh", overflowY: "auto", paddingRight: 4 }}>
              <Table size="sm" responsive style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={tableHeadCellStyle}>Transportadora</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "center" }}>Status</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Última recepção</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Recepções 30d</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Rejeitados 30d</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "center" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {canais.map((canal) => (
                    <tr key={canal.canalId}>
                      <td style={tableCellStyle}>
                        <div style={{ fontWeight: 600 }}>{canal.transportadora}</div>
                        {canal.localOrigem ? (
                          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.72rem", fontFamily: "monospace", wordBreak: "break-all" }}>
                            {canal.localOrigem}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "center" }}>
                        <StatusBadge status={canal.status} />
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                        {formatDateLabel(canal.ultimaRecepcao)}
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "right" }}>
                        {canal.recepcoes30d > 0 ? canal.recepcoes30d.toLocaleString("pt-BR") : "-"}
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "right" }}>
                        {canal.rejeitados30d > 0 ? (
                          <span style={{ color: "var(--everlog-status-danger)" }}>{canal.rejeitados30d}</span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => abrirCanal(canal)}
                          style={{ ...autopilotSecondaryButtonStyle, padding: "0.2rem 0.6rem", fontSize: "0.72rem", whiteSpace: "nowrap" }}
                        >
                          Ver arquivos
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modal.open} toggle={fecharModal} modalClassName="modal-align-top" fade={false} size="lg">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1rem 1.25rem 0.75rem",
            borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
            background: "var(--everlog-card-hero-bg)",
          }}
        >
          <div>
            <div style={{ ...autopilotEyebrowStyle, marginBottom: "0.15rem" }}>Canal SFTP</div>
            <div style={{ ...autopilotTitleStyle, fontSize: "1rem" }}>{modal.canal?.transportadora || "Arquivos do canal"}</div>
            {modal.canal?.localOrigem ? (
              <div style={{ ...autopilotDescriptionStyle, marginTop: "0.15rem", fontSize: "0.75rem" }}>{modal.canal.localOrigem}</div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={fecharModal}
            style={{ background: "transparent", border: "none", color: AUTOPILOT_COLORS.muted, fontSize: "1.25rem", cursor: "pointer", lineHeight: 1, padding: "0.25rem" }}
          >
            ×
          </button>
        </div>
        <ModalBody style={{ ...autopilotBodyStyle, padding: "1rem 1.25rem" }}>
          <div style={{ display: "flex", gap: "0.35rem", marginBottom: "1rem" }}>
            {[
              { id: "pendentes", label: "Pendentes no SFTP" },
              { id: "historico", label: "Histórico de recepção" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => trocarAbaModal(t.id)}
                style={{
                  padding: "0.3rem 0.85rem",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  borderRadius: 6,
                  border: `1px solid ${modal.tab === t.id ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.border}`,
                  background: modal.tab === t.id ? `color-mix(in srgb, ${AUTOPILOT_COLORS.accent} 13%, transparent)` : "transparent",
                  color: modal.tab === t.id ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.muted,
                  cursor: "pointer",
                }}
              >
                {t.label}
                {t.id === "pendentes" && !modal.loadingPendentes && modal.pendentes.length > 0 ? (
                  <span style={{ marginLeft: "0.4rem", background: "var(--everlog-status-danger)", color: "var(--everlog-on-accent-text)", borderRadius: 10, padding: "0.05rem 0.45rem", fontSize: "0.7rem" }}>
                    {modal.pendentes.length}
                  </span>
                ) : null}
              </button>
            ))}
          </div>

          {modal.tab === "pendentes" ? (
            modal.loadingPendentes ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
                <ClipLoader size={28} color={AUTOPILOT_COLORS.accent} />
              </div>
            ) : modal.errorPendentes ? (
              <div style={{ color: "var(--everlog-status-danger)", fontSize: "0.85rem", padding: "0.5rem 0" }}>{modal.errorPendentes}</div>
            ) : modal.pendentes.length === 0 ? (
              <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", padding: "0.5rem 0" }}>
                Nenhum arquivo pendente no diretório SFTP deste canal.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginBottom: "0.6rem" }}>
                  {modal.pendentes.length} arquivo{modal.pendentes.length !== 1 ? "s" : ""} aguardando importação
                </div>
                <Table size="sm" responsive style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th style={tableHeadCellStyle}>Nome do arquivo</th>
                      <th style={{ ...tableHeadCellStyle, textAlign: "center" }}>Modificado em</th>
                      <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Tamanho</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modal.pendentes.map((f) => (
                      <tr key={f.nomeArquivo}>
                        <td style={{ ...tableCellStyle, fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}>{f.nomeArquivo}</td>
                        <td style={{ ...tableCellStyle, textAlign: "center", whiteSpace: "nowrap" }}>{formatDateTime(f.modificadoEm)}</td>
                        <td style={{ ...tableCellStyle, textAlign: "right" }}>{f.tamanho != null ? `${(f.tamanho / 1024).toFixed(1)} KB` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )
          ) : modal.loadingRecepcoes ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
              <ClipLoader size={28} color={AUTOPILOT_COLORS.accent} />
            </div>
          ) : modal.errorRecepcoes ? (
            <div style={{ color: "var(--everlog-status-danger)", fontSize: "0.85rem", padding: "0.5rem 0" }}>{modal.errorRecepcoes}</div>
          ) : modal.recepcoes.length === 0 ? (
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", padding: "0.5rem 0" }}>
              Nenhum arquivo recebido encontrado para este canal.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table size="sm" responsive style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={tableHeadCellStyle}>Nome do arquivo</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "center" }}>Data</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "center" }}>Situação</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>OK</th>
                    <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>NOK</th>
                  </tr>
                </thead>
                <tbody>
                  {modal.recepcoes.map((rec) => {
                    const badge = SITUACAO_RECEPCAO[rec.situacao] || {
                      label: rec.situacaoLabel || String(rec.situacao),
                      color: "var(--everlog-status-neutral)",
                    };
                    return (
                      <tr key={rec.id}>
                        <td style={{ ...tableCellStyle, fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}>{rec.nomeArquivo || "-"}</td>
                        <td style={{ ...tableCellStyle, textAlign: "center", whiteSpace: "nowrap" }}>{formatDateTime(rec.dataRecepcao)}</td>
                        <td style={{ ...tableCellStyle, textAlign: "center" }}>
                          <span
                            style={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              padding: "0.15rem 0.5rem",
                              borderRadius: 4,
                              color: badge.color,
                              border: `1px solid ${badge.color}`,
                              background: `color-mix(in srgb, ${badge.color} 13%, transparent)`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: "right" }}>
                          {rec.registrosOK != null ? <span style={{ color: "var(--everlog-status-success)" }}>{rec.registrosOK.toLocaleString("pt-BR")}</span> : "-"}
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: "right" }}>
                          {rec.registrosNOK != null && rec.registrosNOK > 0 ? <span style={{ color: "var(--everlog-status-danger)" }}>{rec.registrosNOK.toLocaleString("pt-BR")}</span> : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
}
